import React, { useState } from "react";
import {
  Spinner,
  SimpleButton,
  LoadingButton,
  Input,
  Select,
  TextArea,
  FormDialog,
  Tabs,
  Modal,
  Toast,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsCheckCircleFill, BsXCircleFill, BsTrash } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { usePubblicazioni, useChecklistPubblicazioni } from "../hooks/useTrasparenza";
import { useAuthUser } from "../hooks/useAuthUser";

const TIPI = [
  { value: "", label: "Tutti i tipi" },
  { value: "programma_triennale",           label: "Programma Triennale PPP" },
  { value: "avviso_pubblico",               label: "Avviso Pubblico per Proposte" },
  { value: "notizia_presentazione_proposta",label: "Notizia Presentazione Proposta" },
  { value: "proposta_interesse_pubblico",   label: "Proposta di Interesse Pubblico" },
  { value: "integrazioni_documentali",      label: "Integrazioni Documentali" },
  { value: "esito_valutazione_comparativa", label: "Esito Valutazione Comparativa" },
  { value: "provvedimento_ammissione",      label: "Provvedimento Ammissione/Respingimento" },
  { value: "documentazione_gara",          label: "Documentazione di Gara" },
  { value: "altro",                        label: "Altro" },
];

const CANALI = [
  { value: "",                         label: "Tutti i canali" },
  { value: "sito_istituzionale",       label: "Sito Istituzionale" },
  { value: "amministrazione_trasparente", label: "Amministrazione Trasparente" },
  { value: "bdncp",                    label: "BDNCP" },
  { value: "guue",                     label: "GUUE" },
  { value: "bdncp_guue",               label: "BDNCP + GUUE" },
];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function CanaleBadge({ canale }) {
  const map = {
    sito_istituzionale:        { cls: "bg-blue-100 text-blue-700",   label: "Sito Ist." },
    amministrazione_trasparente: { cls: "bg-purple-100 text-purple-700", label: "Amm. Trasp." },
    bdncp:                     { cls: "bg-orange-100 text-orange-700", label: "BDNCP" },
    guue:                      { cls: "bg-red-100 text-red-700",      label: "GUUE" },
    bdncp_guue:                { cls: "bg-red-100 text-red-700",      label: "BDNCP+GUUE" },
  };
  const cfg = map[canale] ?? { cls: "bg-gray-100 text-gray-600", label: canale };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function LogTable({ procedimentoId, filtri, onMutate }) {
  const { pubblicazioni, loading, refresh } = usePubblicazioni(procedimentoId, filtri);
  const { can } = useAuthUser();
  const api = usePppApi();
  const { addToast, addModal, _locate } = useStateContext();
  const [eliminando, setEliminando] = useState(null);

  function handleElimina(logId) {
    addModal(
      <Modal
        title={_locate("ppp_trasparenza_elimina_titolo")}
        content={_locate("ppp_trasparenza_elimina_messaggio")}
        confirm={_locate("ppp_elimina")}
        style="danger"
        onClick={() => {
          setEliminando(logId);
          api.eliminaPubblicazione(procedimentoId, logId, {
            onSuccess: () => {
              setEliminando(null);
              addToast(<Toast duration={4} content={_locate("ppp_trasparenza_eliminata")} />);
              refresh();
              onMutate?.();
            },
            onFormError: () => { setEliminando(null); },
          });
        }}
      />
    );
  }

  if (loading) return <Spinner />;

  if (!pubblicazioni.length) {
    return (
      <p className="text-gray-500 text-sm mt-4">
        {_locate("ppp_trasparenza_nessuna")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b text-left text-gray-500 text-xs uppercase">
            <th className="py-2 pr-4">Data</th>
            <th className="py-2 pr-4">Tipo</th>
            <th className="py-2 pr-4">Canale</th>
            <th className="py-2 pr-4">Titolo</th>
            <th className="py-2 pr-4">Fase</th>
            <th className="py-2 pr-4">Modalità</th>
            <th className="py-2 pr-4">Registrato da</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {pubblicazioni.map((p) => (
            <tr key={p.id} className="border-b hover:bg-gray-50">
              <td className="py-2 pr-4 whitespace-nowrap">{formatDate(p.data_pubblicazione)}</td>
              <td className="py-2 pr-4 max-w-[180px] truncate" title={p.tipo_pubblicazione_label}>
                {p.tipo_pubblicazione_label}
              </td>
              <td className="py-2 pr-4">
                <CanaleBadge canale={p.canale} />
              </td>
              <td className="py-2 pr-4 max-w-[200px]">
                {p.url_pubblicazione ? (
                  <a
                    href={p.url_pubblicazione}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline truncate block"
                    title={p.titolo}
                  >
                    {p.titolo}
                  </a>
                ) : (
                  <span className="truncate block" title={p.titolo}>{p.titolo}</span>
                )}
              </td>
              <td className="py-2 pr-4 text-gray-500">{p.fase ?? "—"}</td>
              <td className="py-2 pr-4">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  p.modalita === "automatica"
                    ? "bg-gray-100 text-gray-500"
                    : "bg-blue-50 text-blue-600"
                }`}>
                  {p.modalita === "automatica" ? "Auto" : "Manuale"}
                </span>
              </td>
              <td className="py-2 pr-4 text-gray-500">{p.registrato_da ?? "—"}</td>
              <td className="py-2">
                {can("ppp.trasparenza.edit") && p.modalita !== "automatica" && (
                  <LoadingButton
                    isLoading={eliminando === p.id}
                    customFunc={() => handleElimina(p.id)}
                    text={<BsTrash size={14} />}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChecklistTab({ checklist, loading }) {
  const { _locate } = useStateContext();

  if (loading) return <Spinner />;
  if (!checklist) return null;

  return (
    <div className="mt-4 space-y-4">
      {/* Riepilogo */}
      <div className={`rounded-lg px-4 py-3 flex items-center gap-3 ${
        checklist.completo ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"
      }`}>
        {checklist.completo
          ? <BsCheckCircleFill size={18} />
          : <BsXCircleFill size={18} />}
        <span className="text-sm font-medium">
          {checklist.completo
            ? _locate("ppp_trasparenza_checklist_completa")
            : _locate("ppp_trasparenza_checklist_incompleta")}
        </span>
        <span className="ml-auto text-sm">
          {checklist.completate}/{checklist.totale_obbligatorie} obblighi completati
        </span>
      </div>

      {/* Voci per fase */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b text-left text-gray-500 text-xs uppercase">
              <th className="py-2 pr-4">Fase</th>
              <th className="py-2 pr-4">Tipo atto</th>
              <th className="py-2 pr-4">Canale obbligatorio</th>
              <th className="py-2 pr-4">Stato</th>
              <th className="py-2 pr-4">Data pubblicazione</th>
              <th className="py-2">URL</th>
            </tr>
          </thead>
          <tbody>
            {checklist.voci.map((v, i) => (
              <tr key={i} className={`border-b ${v.eseguita ? "" : "bg-yellow-50"}`}>
                <td className="py-2 pr-4 text-gray-500">{v.fase || "—"}</td>
                <td className="py-2 pr-4">{v.tipo_pubblicazione_label}</td>
                <td className="py-2 pr-4">
                  <CanaleBadge canale={v.canale_obbligatorio} />
                </td>
                <td className="py-2 pr-4">
                  {v.eseguita
                    ? <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><BsCheckCircleFill /> Eseguita</span>
                    : <span className="flex items-center gap-1 text-yellow-600 text-xs font-medium"><BsXCircleFill /> Mancante</span>
                  }
                </td>
                <td className="py-2 pr-4 text-gray-500">{formatDate(v.data_pubblicazione)}</td>
                <td className="py-2">
                  {v.url_pubblicazione
                    ? <a href={v.url_pubblicazione} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs">Apri</a>
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TrasparenzaPage({ procedimentoId }) {
  const { _locate, addToast, setOpenForm } = useStateContext();
  const { can } = useAuthUser();
  const api = usePppApi();
  const { checklist, loading: checklistLoading, refresh: refreshChecklist } = useChecklistPubblicazioni(procedimentoId);
  const [filtri, setFiltri] = useState({});
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [form, setForm] = useState({
    tipo_pubblicazione: "",
    canale: "",
    titolo: "",
    data_pubblicazione: new Date().toISOString().slice(0, 10),
    url_pubblicazione: "",
    riferimento_normativo: "",
    note: "",
  });

  const filtriConRefresh = { ...filtri, _key: refreshKey };

  function handleSalva() {
    setSaving(true);
    api.registraPubblicazione(procedimentoId, form, {
      onSuccess: () => {
        setSaving(false);
        setOpenForm(false);
        setForm({
          tipo_pubblicazione: "",
          canale: "",
          titolo: "",
          data_pubblicazione: new Date().toISOString().slice(0, 10),
          url_pubblicazione: "",
          riferimento_normativo: "",
          note: "",
        });
        addToast(<Toast duration={4} content={_locate("ppp_trasparenza_registrata")} />);
        setRefreshKey((k) => k + 1);
        refreshChecklist();
      },
      onFormError: () => setSaving(false),
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            {_locate("ppp_trasparenza_titolo")}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {_locate("ppp_trasparenza_descrizione")}
          </p>
        </div>
        {can("ppp.trasparenza.register") && (
          <SimpleButton
            customFunc={() => setOpenForm(true)}
            text={_locate("ppp_trasparenza_registra_btn")}
          />
        )}
      </div>

      {/* Tabs Veda */}
      <Tabs
        tablist={[
          {
            tabname: _locate("ppp_trasparenza_tab_log"),
            content: (
              <div>
                <p className="text-sm text-gray-500 mt-2">
                  {_locate("ppp_trasparenza_log_hint")}
                </p>
                <div className="flex flex-wrap gap-3 mt-3">
                  <Select
                    label={_locate("ppp_trasparenza_filtro_tipo")}
                    value={filtri.tipoPubblicazione ?? ""}
                    onChange={(e) => setFiltri((f) => ({ ...f, tipoPubblicazione: e.target.value || undefined }))}
                    options={TIPI}
                  />
                  <Select
                    label={_locate("ppp_trasparenza_filtro_canale")}
                    value={filtri.canale ?? ""}
                    onChange={(e) => setFiltri((f) => ({ ...f, canale: e.target.value || undefined }))}
                    options={CANALI}
                  />
                </div>
                <LogTable procedimentoId={procedimentoId} filtri={filtriConRefresh} onMutate={refreshChecklist} />
              </div>
            ),
          },
          {
            tabname: _locate("ppp_trasparenza_tab_checklist"),
            content: <ChecklistTab checklist={checklist} loading={checklistLoading} />,
          },
        ]}
        activetab={0}
      />

      {/* Form: Registra pubblicazione */}
      <FormDialog title={_locate("ppp_trasparenza_registra_titolo")}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_tipo_label")} *
              </label>
              <Select
                value={form.tipo_pubblicazione}
                onChange={(e) => setForm((f) => ({ ...f, tipo_pubblicazione: e.target.value }))}
                options={[{ value: "", label: "— seleziona —" }, ...TIPI.slice(1)]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_canale_label")} *
              </label>
              <Select
                value={form.canale}
                onChange={(e) => setForm((f) => ({ ...f, canale: e.target.value }))}
                options={[{ value: "", label: "— seleziona —" }, ...CANALI.slice(1)]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_titolo_label")} *
              </label>
              <Input
                value={form.titolo}
                onChange={(e) => setForm((f) => ({ ...f, titolo: e.target.value }))}
                maxLength={255}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_data_label")} *
              </label>
              <Input
                type="date"
                value={form.data_pubblicazione}
                onChange={(e) => setForm((f) => ({ ...f, data_pubblicazione: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_url_label")}
              </label>
              <Input
                type="url"
                value={form.url_pubblicazione}
                onChange={(e) => setForm((f) => ({ ...f, url_pubblicazione: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_riferimento_label")}
              </label>
              <Input
                value={form.riferimento_normativo}
                onChange={(e) => setForm((f) => ({ ...f, riferimento_normativo: e.target.value }))}
                placeholder="es. art. 28 D.lgs 36/2023"
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {_locate("ppp_trasparenza_note_label")}
              </label>
              <TextArea
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <SimpleButton
                customFunc={() => setOpenForm(false)}
                text={_locate("ppp_form_annulla")}
              />
              <LoadingButton
                isLoading={saving}
                customFunc={handleSalva}
                text={_locate("ppp_trasparenza_registra_btn")}
              />
            </div>
          </div>
        </FormDialog>
    </div>
  );
}
